import axios from 'axios';
import { put, call, select } from 'redux-saga/effects';
import queryString from 'query-string';

// CONSTANTS
import * as endpoints from '@/constants/api';

// UTILITY
import { getLink } from '@/utility/routes';
import { showErrorNotification } from '@/utility/saga';
import { pushSuccessNotification } from '@/utility/snackbar';
import { getFirstParamsFromDictionary } from '@/utility/reports';

// LOCALIZATION
import { t } from '@/locale/i18n';

// STORE
import { ReportsActions } from '@/store/actions';

const {
  getReports,
  getReportDetails,
  generateReport,
  setReportUserFields,
  getReportDictionary,
  getScheduleSettings,
  saveScheduleSettings,
  extendInvite,
} = ReportsActions;

const getReportsDictionary = ({ reports }) => reports.dictionary;

export function* getReportsSaga() {
  yield put({ type: getReports.types.START });

  try {
    const { data: respData } = yield axios.get(endpoints.REPORTS_GET_LIST);

    yield put({
      type: getReports.types.SUCCESS,
      payload: {
        data: respData.report_types,
        periods: respData.periods,
      },
    });

    return respData;
  } catch (error) {
    yield call(showErrorNotification, error);

    yield put({
      type: getReports.types.FAIL,
    });

    return null;
  }
}

export function* getReportDictionarySaga(action) {
  yield put({ type: getReportDictionary.types.START });

  const { report_type, params } = action.payload;
  const url = getLink(endpoints.REPORTS_GET_DICTIONARY, { report_type });

  try {
    const { data: respData } = yield axios.get(url, { params });

    yield put({
      type: getReportDictionary.types.SUCCESS,
      payload: {
        dictionary: respData,
      },
    });

    return respData;
  } catch (error) {
    yield call(showErrorNotification, error);

    yield put({
      type: getReportDictionary.types.FAIL,
    });

    return null;
  }
}

export function* getReportDetailsSaga(action) {
  yield put({ type: getReportDetails.types.START });

  const { report_type, params } = action.payload;
  const url = getLink(endpoints.REPORTS_GET_DETAILS, { report_type });

  const reportsDictionary = yield select(getReportsDictionary);
  let loadDictionary = false;
  let period = '';

  try {
    if (!Object.keys(reportsDictionary).length) {
      const dictionaryRespData = yield call(getReportDictionarySaga, {
        payload: { report_type, params: { partner_id: params.partner_id } },
      });

      loadDictionary = true;

      if (dictionaryRespData) {
        const firstParams = getFirstParamsFromDictionary(dictionaryRespData);

        // Set periods
        if (firstParams.period && params?.date_from === undefined) {
          params.date_from = firstParams.date_from;
          params.date_to = firstParams.date_to;

          period = firstParams;
        }

        // Set partner
        if (firstParams.partner_id !== undefined && params?.partner_id === undefined) {
          params.partner_id = firstParams.partner_id;
        }

        // Set partner
        if (firstParams.reward_id !== undefined && params?.reward_id === undefined) {
          params.reward_id = firstParams.reward_id;
        }

        // Set statuses
        if (firstParams.statuses && params?.statuses === undefined) {
          params.statuses = firstParams.statuses;
        }

        // Set reward status
        if (firstParams.rewardStatus && params?.reward_statuses === undefined) {
          params.reward_statuses = firstParams.rewardStatus;
        }

        // Set date range type
        if (firstParams.dateRangeType && params?.date_range_type === undefined) {
          params.date_range_type = firstParams.dateRangeType;
        }
      }
    }

    const { data: respData } = yield axios.get(url, {
      params,
      paramsSerializer: paramsData => {
        return queryString.stringify(paramsData, {
          skipNull: true,
          arrayFormat: 'bracket',
          skipEmptyString: true,
        });
      },
    });

    yield put({
      type: getReportDetails.types.SUCCESS,
      payload: {
        ...(loadDictionary && { initFilterParams: { ...params, period } }),
        reportInfo: { ...(respData.report_info && { ...respData.report_info, report_type }) },
        headers: respData.headers,
        data: respData.data,
        meta: respData.meta,
      },
    });
  } catch (error) {
    yield call(showErrorNotification, error);

    yield put({
      type: getReportDetails.types.FAIL,
    });
  }
}

export function* downloadReportSaga(action) {
  yield put({ type: generateReport.types.START });

  const { report_type, params } = action.payload;
  const url = getLink(endpoints.REPORTS_GENERATE_REPORT, { report_type });

  try {
    yield axios
      .post(url, null, {
        params,
        paramsSerializer: paramsData => {
          return queryString.stringify(paramsData, {
            encode: false,
            skipNull: true,
            arrayFormat: 'bracket',
            skipEmptyString: true,
          });
        },
      })
      .then(response => {
        const type = response.headers['content-type'];
        const blob = new Blob([response.data], { type, encoding: 'UTF-8' });
        const link = document.createElement('a');

        link.href = window.URL.createObjectURL(blob);
        link.download = `report-${report_type}.csv`;
        link.click();
      });

    yield put({
      type: generateReport.types.SUCCESS,
    });
  } catch (error) {
    yield call(showErrorNotification, error);

    yield put({
      type: generateReport.types.FAIL,
    });
  }
}

export function* setReportUserFieldsSaga(action) {
  yield put({ type: setReportUserFields.types.START });

  const { params } = action.payload;
  const url = endpoints.REPORTS_USER_FIELDS;

  try {
    yield axios.post(url, params);

    yield put({
      type: setReportUserFields.types.SUCCESS,
    });
  } catch (error) {
    yield call(showErrorNotification, error);

    yield put({
      type: setReportUserFields.types.FAIL,
    });
  }
}

export function* getScheduleSettingsSaga(action) {
  yield put({ type: getScheduleSettings.types.START });

  const { report_type } = action.payload;
  const url = getLink(endpoints.REPORTS_GET_SCHEDULE_SETTINGS, { report_type });

  try {
    const { data: respData } = yield axios.get(url);

    yield put({
      type: getScheduleSettings.types.SUCCESS,
      payload: {
        data: respData,
      },
    });
  } catch (error) {
    yield call(showErrorNotification, error);

    yield put({
      type: getScheduleSettings.types.FAIL,
    });
  }
}

export function* saveScheduleSettingsSaga(action) {
  yield put({ type: saveScheduleSettings.types.START });

  const { report_type, data, onSuccess } = action.payload;
  const url = getLink(endpoints.REPORTS_SAVE_SCHEDULE_SETTINGS, { report_type });

  try {
    yield axios.post(url, data);

    yield put({
      type: saveScheduleSettings.types.SUCCESS,
    });

    if (onSuccess) onSuccess();

    pushSuccessNotification(t('notification.saveSettingsSuccess'));
  } catch (error) {
    yield call(showErrorNotification, error);

    yield put({
      type: saveScheduleSettings.types.FAIL,
    });
  }
}

export function* extendInviteSaga(action) {
  yield put({ type: extendInvite.types.START });

  const { invite_id, onSuccess } = action.payload;
  const url = getLink(endpoints.SR_INVITED_USERS_EXTEND_INVITE, { invite_id });

  try {
    const { data: respData } = yield axios.post(url);

    yield put({
      type: extendInvite.types.SUCCESS,
    });

    pushSuccessNotification(
      t('notification.inviteExtended', { date: respData?.invite?.activation_deadline }),
    );

    if (onSuccess) onSuccess();
  } catch (error) {
    yield call(showErrorNotification, error);

    yield put({
      type: extendInvite.types.FAIL,
    });
  }
}

export default getReportsSaga;
